import { BrowserRouter, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useEffect, useState, type ReactNode } from 'react';
import { TelegramProvider, useTelegram } from './contexts/TelegramContext';
import { IdentityProvider, type CoreIdentity } from '@core/contexts/IdentityContext';
import { ProfileProvider } from '@core/contexts/ProfileContext';
import { VenueProvider, useVenue } from '@core/contexts/VenueContext';
import { ConsentGate } from '@core/components/ConsentGate';
import { Skeleton } from '@core/components/Skeleton';
import { VenueFooter } from '@core/components/VenueFooter';
import { BrandLogo } from '@core/components/BrandLogo';
import { brandLogoUrl } from '@core/hooks/useBranding';
import { CoreRoutes } from '@core/routes';
import { getVenueSlug } from './utils/venueSlug';
import { PhoneFirstGate } from './components/PhoneFirstGate';

/** Мост Telegram → identity ядра: telegramId известен сразу; uuid придёт из профиля.
 * Пока клиент на экране номера (phoneRequired), сессии нет — профиль не грузится. */
function IdentityBridge({ children }: { children: ReactNode }) {
  const { user: tgUser, isReady, phoneRequired } = useTelegram();
  const identity: CoreIdentity = {
    status: !isReady || phoneRequired ? 'loading' : tgUser ? 'authenticated' : 'anonymous',
    telegramId: tgUser?.id ?? null,
    userId: null,
    firstName: tgUser?.firstName ?? null,
  };
  return (
    <IdentityProvider value={identity}>
      <ProfileProvider>{children}</ProfileProvider>
    </IdentityProvider>
  );
}

/**
 * Слаг из Telegram start_param может отдать 'demo'-фолбэк, пока SDK не готов —
 * ретраим с нарастающей задержкой (перенос retry-логики старого VenueContext).
 */
function useResolvedVenueSlug(): { slug: string; settled: boolean } {
  const [slug, setSlug] = useState(() => getVenueSlug());
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (slug !== 'demo' || retry >= 5) return;
    const timer = setTimeout(() => {
      const next = getVenueSlug();
      if (next !== 'demo') {
        setSlug(next);
      } else {
        setRetry((r) => r + 1);
      }
    }, 200 * (retry + 1));
    return () => clearTimeout(timer);
  }, [slug, retry]);

  return { slug, settled: slug !== 'demo' || retry >= 5 };
}

function VenueLogo() {
  const { venue } = useVenue();

  // Нет логотипа или он не загрузился — полосы нет (битая иконка не показывается).
  return (
    <BrandLogo
      src={brandLogoUrl(venue?.branding)}
      alt={venue?.name ?? ''}
      className="block h-10 max-w-full mx-auto"
      frame={(img) => <div className="sticky top-0 z-10 bg-page border-b border-border p-3">{img}</div>}
    />
  );
}

function AppContent() {
  const { loading, error } = useVenue();
  const location = useLocation();

  // Hide VenueLogo on pages with their own sticky headers
  const hideVenueLogo =
    location.pathname.startsWith('/menu') ||
    location.pathname.startsWith('/game') ||
    location.pathname.startsWith('/notifications');

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-brand p-4 text-red-800">
          <h2 className="font-semibold mb-1">Ошибка загрузки</h2>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page text-text-main">
      {!hideVenueLogo && <VenueLogo />}

      {/* Экраны — общие для мини-аппа и PWA (одна карта в ядре). В мини-аппе клиент есть всегда
          (TelegramIdentityGate), поэтому личные экраны без гварда. */}
      <CoreRoutes />

      {/* Venue name in footer */}
      <VenueFooter />

      {/* Global toast notifications */}
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--brand-bg)',
            color: 'var(--brand-text)',
            border: '1px solid var(--brand-secondary-bg)',
            borderRadius: 'var(--brand-radius)',
          },
          success: {
            iconTheme: {
              primary: 'var(--brand-accent)',
              secondary: 'var(--brand-bg)',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: 'var(--brand-bg)',
            },
          },
        }}
      />
    </div>
  );
}

/**
 * Гейт идентичности: без Telegram-пользователя мини-ап НЕ показывает профиль.
 *
 * Раньше в этом случае рисовался дефолтный экран — 0 баллов и уровень Bronze, — и клиент
 * с 1832 баллами на счету видел «ноль» (инцидент MELMAN, август 2026). Для программы
 * лояльности это худший из отказов: человек решает, что баллы сгорели. Показываем честную
 * ошибку с возможностью перезагрузиться.
 */
function TelegramIdentityGate({ children }: { children: ReactNode }) {
  const { user, isReady } = useTelegram();
  const location = useLocation();

  // Правовые страницы открыты всегда: ссылки на них уходят наружу и не требуют личности.
  if (location.pathname.startsWith('/legal/')) {
    return <>{children}</>;
  }

  if (!isReady) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-page text-text-main flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-4">
          <h2 className="text-lg font-semibold">Не удалось определить пользователя</h2>
          <p className="text-sm text-text-muted">
            Ваши баллы на месте — приложение просто не получило данные Telegram. Откройте
            программу лояльности через меню бота или обновите страницу.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full rounded-brand bg-brand-accent px-4 py-3 font-medium text-brand-bg"
          >
            Обновить
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function VenueBoundary({ children }: { children: (slug: string) => ReactNode }) {
  const { slug, settled } = useResolvedVenueSlug();

  if (!settled) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  return <>{children(slug)}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <TelegramProvider>
        <IdentityBridge>
          <TelegramIdentityGate>
            <VenueBoundary>
              {(slug) => (
                <VenueProvider slug={slug}>
                  <PhoneFirstGate>
                    <ConsentGate>
                      <AppContent />
                    </ConsentGate>
                  </PhoneFirstGate>
                </VenueProvider>
              )}
            </VenueBoundary>
          </TelegramIdentityGate>
        </IdentityBridge>
      </TelegramProvider>
    </BrowserRouter>
  );
}
